import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";
import { startLoading, stopLoading } from "./commonSlice";
import { archiveRecords } from "../constant/utils/commonUtils";
// ✅ Fetch All Companies
export const fetchAllCompanies = createAsyncThunk(
  "companies/fetchAllCompanies",
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company")
        .select(`
          *,
          seta_template(seta_name),
          company_user!company_user_company_id_fkey(
            id,
            first_name,
            last_name,
            email,
            role
          )
        `)
        .or("status.is.null,status.neq.archived");
      if (error) throw error;
      return data; // Return the list of companies
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Add New Company
export const addCompany = createAsyncThunk(
  "companies/addCompany",
  async (companyData, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data, error } = await supabase
        .from("company")
        .insert([companyData])
        .select()
        .single();
      if (error) throw error;
      dispatch(stopLoading());
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Get Company By ID (For Editing)
export const getCompanyById = createAsyncThunk(
  "companies/getCompanyById",
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data, error } = await supabase
        .from("company")
        .select()
        .eq("id", companyId)
        .single();
      dispatch(stopLoading());
      if (error && error.code !== "PGRST116") {
        throw error;
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Get Company By Registration Number
export const getCompanyByRegistrationNumber = createAsyncThunk(
  "companies/getCompanyByRegistrationNumber",
  async (company_registration_number, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("company")
        .select("id")
        .eq("company_registration_number", company_registration_number)
        .single();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Update Company
export const updateCompany = createAsyncThunk(
  "companies/updateCompany",
  async ({ companyId, ...updatedData }, { rejectWithValue }) => {
    try {
      // company table has no user_id; callers may accidentally merge SDF fields
      const { user_id: _omitUserId, ...payload } = updatedData;
      const { data, error } = await supabase
        .from("company")
        .update(payload)
        .eq("id", companyId)
        .select();
      if (error) throw error;
      return data[0];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Delete Company
export const deleteCompany = createAsyncThunk(
  "companies/deleteCompany",
  async (companyId, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from("company")
        .delete()
        .eq("id", companyId);
      if (error) throw error;
      return companyId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ Archive Company
export const archiveCompany = createAsyncThunk(
  "companies/archiveCompany",
  async (companyId, { rejectWithValue }) => {
    try {
      const result = await archiveRecords(supabase, "company", "id", companyId);
      if (!result.success) {
        throw new Error(result.error);
      }
      return companyId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getFileStructure = createAsyncThunk(
  "companies/getFileStructure",
  async ({ company_id }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(startLoading());
      const { data, error } = await supabase
        .from("company")
        .select("seta_template(file_structure)") // Using a join to get file_structure from seta table
        .eq("id", company_id)
        .single();
      dispatch(stopLoading());
      if (error) throw error;

      return data?.seta_template?.file_structure?.sheets[0]; // Returning the file_structure directly
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const companySlice = createSlice({
  name: "companies",
  initialState: {
    companies: [],
    loading: false,
    error: null,
    selectedCompany: null,
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch All Companies
      .addCase(fetchAllCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = action.payload;
      })
      .addCase(fetchAllCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Add Company
      .addCase(addCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies.push(action.payload);
      })
      .addCase(addCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Get Company By ID
      .addCase(getCompanyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompanyById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCompany = action.payload;
      })
      .addCase(getCompanyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Update Company
      .addCase(updateCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = state.companies.map((company) =>
          company.id === action.payload.id ? action.payload : company
        );
        state.selectedCompany = null;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Delete Company
      .addCase(deleteCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = state.companies.filter(
          (company) => company.id !== action.payload
        );
        state.selectedCompany = null;
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Archive Company
      .addCase(archiveCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = state.companies.filter(
          (company) => company.id !== action.payload
        );
        state.selectedCompany = null;
      })
      .addCase(archiveCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default companySlice.reducer;
